import { Component, ErrorInfo, ReactNode } from "react";
import { offlineStore } from "../lib/OfflineStore";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
    }

    handleReset = async () => {
        if (!confirm("This will delete all offline books and reset settings. Are you sure?")) return;
        try {
            localStorage.clear();
            const databases = await window.indexedDB.databases();
            for (const db of databases) {
                if (db.name) window.indexedDB.deleteDatabase(db.name);
            }
            window.location.reload();
        } catch (e) {
            alert("Failed to reset fully. Please clear site data manually.");
            window.location.reload();
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    padding: '40px', textAlign: 'center', height: '100vh',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--bg-color)', color: 'var(--text-color)'
                }}>
                    <h2 style={{ fontSize: '2rem', marginBottom: '10px' }}>Something went wrong.</h2>
                    <p style={{ maxWidth: '500px', marginBottom: '30px', opacity: 0.8 }}>
                        The application encountered an unexpected error.
                    </p>

                    <div style={{ display: 'flex', gap: '20px' }}>
                        <button
                            onClick={() => window.location.reload()}
                            style={{ padding: '10px 20px', fontSize: '1rem', cursor: 'pointer' }}
                        >
                            Reload App
                        </button>

                        <button
                            onClick={this.handleReset}
                            style={{
                                padding: '10px 20px', fontSize: '1rem', cursor: 'pointer',
                                background: '#331111', color: '#ff8888', border: '1px solid #ff4444'
                            }}
                        >
                            Reset Data
                        </button>
                    </div>

                    {this.state.error && (
                        <pre style={{
                            marginTop: '40px', padding: '10px', background: '#111',
                            borderRadius: '5px', overflow: 'auto', maxWidth: '80%', textAlign: 'left'
                        }}>
                            {this.state.error.toString()}
                        </pre>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
